import { curSym } from "@/lib/company";
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtTL } from "@/components/StatusBadge";
import { productImage } from "@/lib/images";
import ProductDialog from "@/components/ProductDialog";
import ProxySettings from "@/components/ProxySettings";
import WeeklyMarketResearch from "@/components/WeeklyMarketResearch";
import MarketSourcesSettings from "@/components/MarketSourcesSettings";
import { useAuth } from "@/context/AuthContext";
import Pagination from "@/components/Pagination";
import {
  Sparkles, ExternalLink, TrendingDown, TrendingUp, Search, Mail, Loader2,
  Settings2, RefreshCw, LineChart as LineChartIcon, AlertTriangle, Store, Building2,
  ShoppingCart, PackageX,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend,
} from "recharts";

const TypeChip = ({ t }) => (
  <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${t === "perakende" ? "bg-sky-100 text-sky-700" : "bg-purple-100 text-purple-700"}`}>
    {t === "perakende" ? "PERAKENDE" : t === "karma" ? "KARMA" : "TOPTAN"}
  </span>
);

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

function DevBadge({ pct, dir }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-400">—</span>;
  const above = dir === "above";
  const cls = Math.abs(pct) >= 10 ? (above ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700") : "bg-slate-100 text-slate-500";
  const Icon = above ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular ${cls}`}>
      <Icon className="h-3 w-3" /> {above ? "+" : ""}{pct}%
    </span>
  );
}

function SummaryCard({ testid, icon: Icon, label, value, sub, accent }) {
  return (
    <div data-testid={testid} className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className={`h-4 w-4 ${accent || ""}`} />
        <p className="overline">{label}</p>
      </div>
      <p className="mt-1 font-heading text-2xl font-bold text-slate-900 tabular">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

export default function Opportunities() {
  const { can } = useAuth();
  const [tab, setTab] = useState("market");
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [oppList, setOppList] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);      // opportunity product (ProductDialog)
  const [detail, setDetail] = useState(null);          // market detail {row, data}
  const [emailing, setEmailing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingOne, setRefreshingOne] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [proxyOpen, setProxyOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sources, setSources] = useState(null);
  const openSources = () => {
    setSourcesOpen(true);
    if (!sources) api.get("/market/sources").then((r) => setSources(r.data)).catch(() => {});
  };
  const [procure, setProcure] = useState(null);
  const [mPage, setMPage] = useState(1);
  const [mSize, setMSize] = useState(50);
  const [mCat, setMCat] = useState("");

  const loadMarket = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (filter === "alerts") p.set("filter", "alerts");
    api.get(`/market-research?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [q, filter]);

  const loadSummary = useCallback(() => {
    api.get("/market-research/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { const t = setTimeout(loadMarket, 250); return () => clearTimeout(t); }, [loadMarket]);
  useEffect(() => {
    if (tab === "opportunities" && oppList === null) {
      api.get("/opportunities").then((r) => setOppList(r.data)).catch(() => setOppList([]));
    }
  }, [tab, oppList]);

  const sendEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/opportunities/email");
      toast.success(`E-posta gönderildi: ${data.sent} yöneticiye (${data.items} fırsat)`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setEmailing(false); }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post("/market-research/refresh?scope=tracked");
      toast.success("İzlenen ve uyarılı ürünler arka planda güncelleniyor (tarama kotasını korumak için tümü değil, izlenenler).");
      setTimeout(() => { loadSummary(); loadMarket(); }, 8000);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTimeout(() => setRefreshing(false), 1500); }
  };

  const openMarketDetail = async (row) => {
    setDetail({ row, data: null });
    try {
      const { data } = await api.get(`/market-research/${row.id}/history`);
      setDetail({ row, data });
    } catch { setDetail({ row, data: { history: [], sources: [] } }); }
  };

  const refreshOne = async () => {
    if (!detail?.row) return;
    const rid = detail.row.id;
    setRefreshingOne(true);
    try {
      await api.post(`/products/${rid}/refresh-market-price`);
      toast.success("Piyasa araştırması başlatıldı. Sonuçlar birkaç dakika içinde güncellenecek.");
      setTimeout(async () => {
        try {
          const { data } = await api.get(`/market-research/${rid}/history`);
          setDetail((d) => (d && d.row?.id === rid ? { ...d, data } : d));
          loadSummary(); loadMarket();
        } catch { /* sessizce geç */ }
      }, 10000);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTimeout(() => setRefreshingOne(false), 1500); }
  };

  const aiRefreshOne = async () => {
    if (!detail?.row) return;
    const rid = detail.row.id;
    setAiBusy(true);
    try {
      const { data } = await api.post(`/products/${rid}/ai-market-price`);
      const avg = data?.market_avg?.avg;
      toast.success(avg ? `Google AI: ortalama ${fmtTL(avg)} bulundu` : "Google AI güncelledi");
      try {
        const { data: h } = await api.get(`/market-research/${rid}/history`);
        setDetail((d) => (d && d.row?.id === rid
          ? { ...d, data: h, row: { ...d.row, market_avg: avg, market_status: "AI",
              market_low: data?.market_avg?.low, market_high: data?.market_avg?.high,
              deviation_pct: data?.market_alert?.deviation_pct, alert: !!data?.market_alert?.active } }
          : d));
      } catch { /* sessizce geç */ }
      loadSummary(); loadMarket();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Google AI fiyat bulamadı"); }
    finally { setAiBusy(false); }
  };

  const openOppProduct = async (id) => {
    const { data } = await api.get(`/products/${id}`);
    setSelected(data);
  };

  const chartData = (detail?.data?.history || []).map((h) => ({
    t: fmtDate(h.at), avg: h.avg, cheapest: h.cheapest_price, cost: h.our_cost,
  }));

  useEffect(() => { setMPage(1); }, [q, filter, mSize, mCat]);
  const categories = rows ? Array.from(new Set(rows.map((r) => r.category || "Diğer"))).sort((a, b) => a.localeCompare(b, "tr")) : [];
  const catSummary = (() => {
    const m = {};
    (rows || []).forEach((r) => {
      const c = r.category || "Diğer";
      if (!m[c]) m[c] = { count: 0, alerts: 0 };
      m[c].count += 1;
      if (r.alert) m[c].alerts += 1;
    });
    return Object.entries(m).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.count - a.count);
  })();
  const filteredRows = rows ? (mCat ? rows.filter((r) => (r.category || "Diğer") === mCat) : rows) : rows;
  const pagedRows = filteredRows ? filteredRows.slice((mPage - 1) * mSize, mPage * mSize) : filteredRows;

  return (
    <div className="space-y-5 fade-in" data-testid="market-research-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-emerald-600" />
          <div>
            <p className="overline">Piyasa Analizi</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Pazar Araştırması</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="refresh-all-btn" onClick={refreshAll} disabled={refreshing} variant="outline" className="border-slate-300">
            {refreshing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} İzlenenleri Yenile
          </Button>
          <Button data-testid="opp-email-btn" onClick={sendEmail} disabled={emailing} className="bg-brand-green text-white hover:bg-brand-green-600">
            {emailing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Yöneticilere E-posta
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard testid="mr-summary-tracked" icon={LineChartIcon} label="Takip Edilen Ürün" value={summary?.tracked ?? "—"} accent="text-brand-green" />
        <SummaryCard testid="mr-summary-alerts" icon={AlertTriangle} label="Uyarı (≥%10 sapma)" value={summary?.alerts ?? "—"}
          sub={summary ? `${summary.above} pahalı · ${summary.below} ucuz` : ""} accent="text-amber-500" />
        <SummaryCard testid="mr-summary-updated" icon={RefreshCw} label="Son Güncelleme" value={summary?.last_refresh ? fmtDate(summary.last_refresh) : "—"}
          sub={summary?.last_run ? `Son tarama: ${summary.last_run.ok || 0}/${summary.last_run.total || 0} kaynaklı` : ""} accent="text-sky-500" />
        <SummaryCard testid="mr-summary-sources" icon={Search} label="Taranan Kaynak" value={summary?.sources_total ?? 19} sub="perakende + toptan site" accent="text-purple-500" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button data-testid="mr-tab-market" onClick={() => setTab("market")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === "market" ? "border-brand-green text-brand-green" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Piyasa Karşılaştırma
        </button>
        <button data-testid="mr-tab-opportunities" onClick={() => setTab("opportunities")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === "opportunities" ? "border-brand-green text-brand-green" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Tedarik Fırsatları
        </button>
        {can("price.read") && (
          <button data-testid="mr-tab-settings" onClick={() => setTab("settings")}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === "settings" ? "border-brand-green text-brand-green" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            Kaynaklar & Ayarlar
          </button>
        )}
      </div>

      {tab === "market" ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="mr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün ara..." className="pl-9" />
            </div>
            <button data-testid="mr-filter-all" onClick={() => setFilter("all")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${filter === "all" ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>Tümü</button>
            <button data-testid="mr-filter-alerts" onClick={() => setFilter("alerts")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${filter === "alerts" ? "bg-amber-500 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>Sadece Uyarılar{summary?.alerts != null ? ` (${summary.alerts})` : ""}</button>
            <select data-testid="mr-category-filter" value={mCat} onChange={(e) => setMCat(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="">Tüm Kategoriler</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {catSummary.length > 0 && (
            <div data-testid="mr-category-summary" className="flex flex-wrap gap-1.5">
              <button data-testid="mr-cat-chip-all" onClick={() => setMCat("")}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${mCat === "" ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                Tümü <span className="tabular opacity-80">{rows?.length || 0}</span>
              </button>
              {catSummary.map((c) => (
                <button key={c.name} data-testid={`mr-cat-chip-${c.name}`} onClick={() => setMCat(c.name === mCat ? "" : c.name)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${mCat === c.name ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                  {c.name} <span className="tabular opacity-80">{c.count}</span>
                  {c.alerts > 0 && <span title="≥%10 sapma uyarısı" className={`rounded-full px-1.5 text-[10px] font-bold ${mCat === c.name ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"}`}>{c.alerts}</span>}
                </button>
              ))}
            </div>
          )}

          <div className="overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Ürün</th>
                    <th className="px-4 py-3 font-medium text-right">Alış Fiyatımız</th>
                    <th className="px-4 py-3 font-medium text-right">Piyasa Ort.</th>
                    <th className="px-4 py-3 font-medium">En Ucuz Kaynak</th>
                    <th className="px-4 py-3 font-medium text-right">Fark</th>
                    <th className="px-4 py-3 font-medium">Son Güncelleme</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows === null ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}><td colSpan={6} className="px-4 py-4"><div className="h-5 animate-pulse rounded bg-slate-100" /></td></tr>
                    ))
                  ) : filteredRows.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                      {mCat ? `"${mCat}" kategorisinde kayıt yok.` : filter === "alerts" ? "≥%10 sapma uyarısı yok." : "Henüz taranmış ürün yok. 'Tümünü Yenile' ile başlatabilirsiniz."}
                    </td></tr>
                  ) : pagedRows.map((r) => (
                    <tr key={r.id} data-testid={`mr-row-${r.id}`} onClick={() => openMarketDetail(r)}
                      className={`cursor-pointer hover:bg-slate-50 ${r.alert ? "bg-amber-50/40" : ""}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.alert && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-800">{r.name}</p>
                            <p className="font-mono text-[10px] text-slate-400">{r.sku}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="tabular font-semibold text-slate-700">{fmtTL(r.our_cost)}</span>
                        {r.cost_missing && (
                          <span data-testid={`mr-cost-missing-${r.id}`} title="Alış maliyeti eksik — sapma yüzdesi güvenilir değil"
                            className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 align-middle text-[10px] font-bold text-red-600">
                            <PackageX className="h-3 w-3" />!
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular text-slate-700">{r.market_avg != null ? fmtTL(r.market_avg) : "—"}</td>
                      <td className="px-4 py-3">
                        {r.cheapest ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                            <TypeChip t={r.cheapest.type} /> <span className="font-medium">{r.cheapest.site}</span>
                            <span className="tabular text-emerald-700">{fmtTL(r.cheapest.price)}</span>
                          </span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right"><DevBadge pct={r.deviation_pct} dir={r.direction} /></td>
                      <td className="px-4 py-3 text-xs text-slate-400">{r.updated_at ? fmtDate(r.updated_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {filteredRows && filteredRows.length > 0 && (
            <Pagination page={mPage} pageSize={mSize} total={filteredRows.length} onPage={setMPage} onPageSize={setMSize} testid="mr-pagination" />
          )}
          <p className="text-[11px] text-slate-400">
            Piyasa ortalaması alış fiyatınızdan çift yönlü ≥%10 saparsa uyarı olarak işaretlenir. Kaynaklar her gece otomatik taranır; engellenen siteler "veri yok" sayılır.
          </p>
        </>
      ) : tab === "opportunities" ? (
        <OpportunitiesTab list={oppList} onOpen={openOppProduct} />
      ) : (
        <div className="space-y-5" data-testid="mr-settings-tab">
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
            <div className="mr-auto min-w-0">
              <p className="font-heading text-sm font-bold text-slate-800">Canlı Fiyat Araçları</p>
              <p className="text-[11px] text-slate-500">Proxy bağlantısı, taranan kaynak listesi ve haftalık otomatik tarama tek yerde.</p>
            </div>
            {can("role.manage") && (
              <Button data-testid="proxy-settings-btn" onClick={() => setProxyOpen(true)} variant="outline" className="border-slate-300">
                <Settings2 className="mr-1.5 h-4 w-4" /> Canlı Fiyat Ayarı (Proxy)
              </Button>
            )}
            <Button data-testid="mr-sources-btn" onClick={openSources} variant="outline" className="border-slate-300">
              <Search className="mr-1.5 h-4 w-4" /> Kaynak Listesi
            </Button>
            {can("price.write") && <WeeklyMarketResearch />}
          </div>
          <MarketSourcesSettings />
        </div>
      )}

      {/* Market detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="mr-detail-dialog" className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {detail.row.name}
                  {detail.row.alert && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700"><AlertTriangle className="h-3 w-3" /> Uyarı</span>}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2">
                  <span className="font-mono">{detail.row.sku}</span>
                  <DevBadge pct={detail.row.deviation_pct} dir={detail.row.direction} />
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-slate-50 p-2 ring-1 ring-inset ring-slate-200">
                    <p className="overline">Alış Fiyatımız</p>
                    <p className="tabular text-sm font-bold text-slate-800">{fmtTL(detail.row.our_cost)}</p>
                  </div>
                  <div className="rounded-md bg-slate-50 p-2 ring-1 ring-inset ring-slate-200">
                    <p className="overline">Piyasa Ort.</p>
                    <p className="tabular text-sm font-bold text-slate-800">{detail.row.market_avg != null ? fmtTL(detail.row.market_avg) : "—"}</p>
                  </div>
                  <div className="rounded-md bg-slate-50 p-2 ring-1 ring-inset ring-slate-200">
                    <p className="overline">En Ucuz</p>
                    <p className="tabular text-sm font-bold text-emerald-700">{detail.row.cheapest ? fmtTL(detail.row.cheapest.price) : "—"}</p>
                  </div>
                </div>

                {/* Price history chart */}
                <div className="rounded-md ring-1 ring-inset ring-slate-200 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><LineChartIcon className="h-3.5 w-3.5" /> Fiyat Geçmişi</p>
                  {detail.data === null ? (
                    <div className="flex h-52 items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                  ) : chartData.length === 0 ? (
                    <div className="flex h-52 items-center justify-center text-xs text-slate-400">Henüz fiyat geçmişi yok.</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                          <XAxis dataKey="t" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" width={50} />
                          <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <ReferenceLine y={detail.row.our_cost} stroke="#f97316" strokeDasharray="4 4" label={{ value: "Alış", fontSize: 10, fill: "#f97316", position: "insideTopLeft" }} />
                          <Line type="monotone" dataKey="avg" name="Piyasa Ort." stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="cheapest" name="En Ucuz" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                      {chartData.length === 1 && <p className="mt-1 text-center text-[10px] text-slate-400">Tek ölçüm — sonraki taramalarla grafik zenginleşecek.</p>}
                    </>
                  )}
                </div>

                {/* Per-source breakdown */}
                {detail.data?.sources?.length > 0 && (
                  <div className="max-h-52 overflow-auto rounded-md ring-1 ring-inset ring-slate-200">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50 text-left text-[11px] text-slate-500">
                        <tr><th className="px-3 py-2">Kaynak</th><th className="px-3 py-2">Tip</th><th className="px-3 py-2 text-right">En Düşük</th><th className="px-3 py-2">Durum</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {detail.data.sources.map((s, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-slate-700">{s.best?.url ? <a href={s.best.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">{s.site} <ExternalLink className="h-3 w-3" /></a> : s.site}</td>
                            <td className="px-3 py-2"><TypeChip t={s.type} /></td>
                            <td className="px-3 py-2 text-right tabular text-slate-700">{s.low != null ? fmtTL(s.low) : "—"}</td>
                            <td className="px-3 py-2">
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${s.status === "OK" ? "bg-emerald-50 text-emerald-600" : s.status === "BLOCKED" ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-400"}`}>
                                {s.status === "OK" ? "veri var" : s.status === "BLOCKED" ? "engellendi" : "veri yok"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {detail.row.cost_missing && (
                  <div data-testid="mr-cost-warning" className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-inset ring-red-200">
                    <PackageX className="h-4 w-4 shrink-0" /> Bu ürünün alış maliyeti tanımlı değil; sapma yüzdesi gerçeği yansıtmayabilir. Lütfen mal kabul/alış fiyatını güncelleyin.
                  </div>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  {can("transfer.create") && (
                    <Button data-testid="mr-procure-btn" onClick={() => setProcure(detail.row)} className="bg-brand-green text-white hover:bg-brand-green-600">
                      <ShoppingCart className="mr-1.5 h-4 w-4" /> Tek Tık Tedarik
                    </Button>
                  )}
                  <Button data-testid="mr-ai-price-btn" onClick={aiRefreshOne} disabled={aiBusy || !can("price.write")}
                    className="bg-brand-orange text-white hover:bg-orange-600">
                    {aiBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Google AI ile Bul
                  </Button>
                  <Button data-testid="mr-refresh-product-btn" onClick={refreshOne} disabled={refreshingOne || !can("price.write")} variant="outline" className="border-slate-300">
                    {refreshingOne ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Bu Ürünü Yenile
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selected && (
        <ProductDialog product={selected} onClose={() => setSelected(null)}
          onUpdate={(u) => setSelected(u)} canPrice={can("price.write")} />
      )}
      <ProxySettings open={proxyOpen} onClose={() => setProxyOpen(false)} />
      <Dialog open={sourcesOpen} onOpenChange={(o) => !o && setSourcesOpen(false)}>
        <DialogContent data-testid="mr-sources-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Search className="h-5 w-5 text-brand-green" /> Canlı Fiyat Kaynakları</DialogTitle>
            <DialogDescription>{sources ? `${sources.total} kaynak · ${sources.unique_domains} benzersiz domain · ${sources.price_capable} fiyat verebilir · ${sources.reference_only} referans-only · Proxy: ${sources.proxy_active ? sources.proxy_masked : "kapalı (doğrudan)"} · Yönetilen API: ${sources.managed_api_active ? "aktif" : "kapalı"}` : "Yükleniyor..."}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md ring-1 ring-inset ring-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                <tr><th className="px-3 py-2 font-medium">Kaynak</th><th className="px-3 py-2 font-medium">Domain</th><th className="px-3 py-2 font-medium">Tür</th><th className="px-3 py-2 font-medium">Fiyat</th><th className="px-3 py-2 font-medium">Durum</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(sources?.sources || []).map((s) => (
                  <tr key={s.source_id} data-testid={`mr-source-${s.source_id}`}>
                    <td className="px-3 py-2 font-medium text-slate-700">{s.display_name}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{s.base_domain}</td>
                    <td className="px-3 py-2"><TypeChip t={s.channel} /></td>
                    <td className="px-3 py-2">{s.price_capable ? <span className="font-medium text-emerald-600">✓ Fiyat</span> : <span className="text-slate-400">Referans</span>}</td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${s.reference_only ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>{s.status}</span></td>
                  </tr>
                ))}
                {sources && (sources.sources || []).length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Kaynak kaydı yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      <ProcureDialog row={procure} onClose={() => setProcure(null)} />
    </div>
  );
}

function OpportunitiesTab({ list, onOpen }) {
  if (list === null) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-40 animate-pulse rounded-md bg-slate-200/60" />)}
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-300 py-16">
        <Search className="mb-2 h-8 w-8 text-slate-300" />
        <p className="text-sm text-slate-500">Şu an bir tedarik fırsatı yok.</p>
        <p className="mt-1 text-xs text-slate-400">Alış fiyatımızdan daha ucuza satan bir site bulunduğunda burada listelenir.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {list.map((p) => {
        const o = p.opportunity;
        return (
          <button key={p.id} data-testid={`opp-card-${p.id}`} onClick={() => onOpen(p.id)}
            className="group flex flex-col overflow-hidden rounded-md bg-white text-left ring-1 ring-inset ring-emerald-200 transition-shadow hover:shadow-md">
            <div className="flex items-center gap-3 border-b border-emerald-100 bg-emerald-50 p-3">
              <img src={productImage(p)} alt="" className="h-11 w-11 rounded-sm object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">{p.name}</p>
                <p className="font-mono text-[10px] text-slate-400">{p.sku}</p>
              </div>
              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-1 text-xs font-bold text-white tabular">
                <TrendingDown className="h-3.5 w-3.5" /> %{o.saving_pct}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 p-3">
              <div><p className="overline flex items-center gap-1"><Store className="h-3 w-3" /> Bizim Alış</p><p className="tabular text-sm font-bold text-slate-700">{fmtTL(o.our_cost)}</p></div>
              <div><p className="overline flex items-center gap-1"><Building2 className="h-3 w-3" /> {o.site} <TypeChip t={o.type} /></p><p className="tabular text-sm font-bold text-emerald-700">{fmtTL(o.price)}</p></div>
            </div>
            <div className="mt-auto flex items-center justify-between border-t border-slate-100 px-3 py-2">
              <span className="text-xs text-slate-500">Kalem başı <b className="tabular text-emerald-700">{fmtTL(o.saving)}</b> tasarruf</span>
              {o.url && (
                <a href={o.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline">Site <ExternalLink className="h-3 w-3" /></a>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ProcureDialog({ row, onClose }) {
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [wh, setWh] = useState("");
  const [qty, setQty] = useState(1);
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
    api.get("/locations").then((r) => setWarehouses((r.data || []).filter((l) => (l.types || [l.type]).includes("warehouse") && l.is_active !== false))).catch(() => {});
    setSupplier(""); setWh(""); setQty(1);
    setCost(row.cheapest?.price || row.our_cost || "");
  }, [row]);

  const submit = async () => {
    if (!supplier) return toast.error("Kayıtlı bir tedarikçi seçin");
    if (!wh) return toast.error("Depo seçin");
    const q = Number(qty), uc = Number(cost);
    if (!q || q <= 0) return toast.error("Miktar girin");
    if (!uc || uc <= 0) return toast.error("Alış fiyatı girin (KDV hariç)");
    setSaving(true);
    const parts = [];
    if (row.cheapest) parts.push(`En ucuz kaynak: ${row.cheapest.site} @ ${fmtTL(row.cheapest.price)}`);
    if (row.market_avg != null) parts.push(`Piyasa ort.: ${fmtTL(row.market_avg)}`);
    if (row.cheapest?.url) parts.push(row.cheapest.url);
    try {
      await api.post("/orders/purchase", {
        warehouse_location_id: wh, supplier,
        note: `Tek-tık tedarik (Pazar Araştırması) · ${parts.join(" · ")}`,
        lines: [{ product_id: row.id, qty: q, unit_cost: uc }],
      });
      toast.success("Satın alma talebi taslağı oluşturuldu ve zorunlu onay kuyruğuna gönderildi.");
      onClose();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mr-procure-dialog" className="max-w-md">
        {row && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading"><ShoppingCart className="h-5 w-5 text-brand-green" /> Tek Tık Tedarik</DialogTitle>
              <DialogDescription><b>{row.name}</b> için satın alma talebi taslağı. Onaylanana kadar uygulanmaz.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {row.cheapest && (
                <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 ring-1 ring-inset ring-emerald-200">
                  En ucuz kaynak: <b>{row.cheapest.site}</b> · <span className="tabular">{fmtTL(row.cheapest.price)}</span> — bu bilgi sipariş notuna eklenir.
                </div>
              )}
              <div>
                <Label>Kayıtlı Tedarikçi *</Label>
                <select data-testid="procure-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">— Tedarikçi seçin —</option>
                  {suppliers.map((s) => <option key={s.id || s.name} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Teslim Deposu *</Label>
                <select data-testid="procure-warehouse" value={wh} onChange={(e) => setWh(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">— Depo seçin —</option>
                  {warehouses.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Miktar *</Label><Input data-testid="procure-qty" type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 tabular" /></div>
                <div><Label>Alış {curSym()} (KDV hariç) *</Label><Input data-testid="procure-cost" type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} className="mt-1 tabular" placeholder="0,00" /></div>
              </div>
              <Button data-testid="procure-submit" onClick={submit} disabled={saving} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Satın Alma Talebi Oluştur (onaya gönder)
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
